import { css } from '@emotion/react'

import type { Theme } from './theme'

/**
 * Global styles for the application
 * These styles are applied to the entire application via Emotion's Global component.
 * Splitting the CSS template into sub-helpers doesn't improve readability — it's a
 * flat reset + base styles block, meant to be read top-to-bottom.
 */
// eslint-disable-next-line max-lines-per-function
export const globalStyles = (theme: Theme) => css`
  /* CSS Reset and Base Styles */
  *,
  *::before,
  *::after {
    box-sizing: border-box;
  }

  html {
    font-size: 16px;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
  }

  body {
    margin: 0;
    padding: 0;
    font-family: ${theme.marko.typography.fontFamilyText};
    font-size: ${theme.marko.typography.fontSizes.medium};
    line-height: 1.5;
    color: ${theme.marko.colors.text};
    background-color: white;
  }

  /* Headings */
  h1,
  h2,
  h3,
  h4,
  h5,
  h6 {
    margin: 0;
    font-family: ${theme.marko.typography.fontFamilyTitle};
    font-weight: ${theme.marko.typography.fontWeights.title};
    line-height: 1.2;
  }

  h1 {
    font-size: ${theme.marko.typography.fontSizes.extraLarge};
  }

  h2 {
    font-size: ${theme.marko.typography.fontSizes.large};
  }

  h3 {
    font-size: ${theme.marko.typography.fontSizes.medium};
  }

  /* Links */
  a {
    color: ${theme.marko.colors.variant.primary.color};
    text-decoration: none;
    transition: color ${theme.marko.variables.animationDuration.normal};

    &:hover {
      color: ${theme.marko.colors.variant.primary.hover};
    }

    &:active {
      color: ${theme.marko.colors.variant.primary.active};
    }
  }

  /* Focus styles for accessibility */
  *:focus-visible {
    outline: 2px solid ${theme.marko.colors.focus};
    outline-offset: 2px;
  }

  /* Remove default button styles */
  button {
    font-family: inherit;
    cursor: pointer;
  }

  /* Form elements */
  input,
  textarea,
  select {
    font-family: inherit;
    font-size: inherit;
  }

  /* Remove default list styles */
  ul,
  ol {
    margin: 0;
    padding: 0;
  }

  /* Paragraph spacing */
  p {
    margin: 0;
  }

  /* Image defaults */
  img {
    max-width: 100%;
    height: auto;
  }

  /* Scrollbar styling (optional, controlled by SuiProvider) */
  ::-webkit-scrollbar {
    width: 8px;
    height: 8px;
  }

  ::-webkit-scrollbar-track {
    background: ${theme.sqwTier2Color.background.neutral.subtlest.default};
  }

  ::-webkit-scrollbar-thumb {
    background: ${theme.sqwTier2Color.border.subtle};
    border-radius: ${theme.marko.variables.borderRadius.small}px;

    &:hover {
      background: ${theme.sqwTier2Color.border.bold};
    }
  }
`
